import { supabase } from '../lib/supabase';

/**
 * Service for managing partner categories
 */

// Convert snake_case from database to camelCase for React
const convertToCamelCase = (category) => {
  if (!category) return null;
  
  return {
    id: category?.id,
    name: category?.name,
    description: category?.description,
    icon: category?.icon,
    isVisible: category?.is_visible,
    partnerCount: category?.partner_count,
    createdAt: category?.created_at,
    updatedAt: category?.updated_at,
  };
};

// Convert camelCase from React to snake_case for database
const convertToSnakeCase = (category) => {
  if (!category) return null;
  
  return {
    name: category?.name,
    description: category?.description,
    icon: category?.icon || 'Building2',
    is_visible: category?.isVisible !== undefined ? category?.isVisible : true,
  };
};

export const partnerCategoryService = {
  /**
   * Get all partner categories with partner counts
   */
  async getAll() {
    try {
      const { data, error } = await supabase
        ?.from('partner_categories')
        ?.select(`
          *,
          partner_count:dealerships(count)
        `)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data to include partner count
      const transformed = data?.map(category => ({
        ...category,
        partner_count: category?.partner_count?.[0]?.count || 0
      }));

      return transformed?.map(convertToCamelCase) || [];
    } catch (error) {
      console.error('Error fetching partner categories:', error);
      throw new Error(error?.message || 'Failed to fetch partner categories');
    }
  },

  /**
   * Get a single category by ID
   */
  async getById(id) {
    try {
      const { data, error } = await supabase
        ?.from('partner_categories')
        ?.select('*')
        ?.eq('id', id)
        ?.single();

      if (error) throw error;

      return convertToCamelCase(data);
    } catch (error) {
      console.error('Error fetching partner category:', error);
      throw new Error(error?.message || 'Failed to fetch partner category');
    }
  },

  /**
   * Create a new partner category
   */
  async create(categoryData) {
    try {
      const dbData = convertToSnakeCase(categoryData);
      
      const { data, error } = await supabase
        ?.from('partner_categories')
        ?.insert([dbData])
        ?.select()
        ?.single();

      if (error) throw error;

      return convertToCamelCase(data);
    } catch (error) {
      console.error('Error creating partner category:', error);
      throw new Error(error?.message || 'Failed to create partner category');
    }
  },

  /**
   * Update an existing partner category
   */
  async update(id, categoryData) {
    try {
      const dbData = convertToSnakeCase(categoryData);
      
      const { data, error } = await supabase
        ?.from('partner_categories')
        ?.update(dbData)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;

      return convertToCamelCase(data);
    } catch (error) {
      console.error('Error updating partner category:', error);
      throw new Error(error?.message || 'Failed to update partner category');
    }
  },

  /**
   * Delete a partner category
   */
  async delete(id) {
    try {
      const { error } = await supabase
        ?.from('partner_categories')
        ?.delete()
        ?.eq('id', id);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting partner category:', error);
      throw new Error(error?.message || 'Failed to delete partner category');
    }
  },

  /**
   * Toggle category visibility
   */
  async toggleVisibility(id, isVisible) {
    try {
      const { data, error } = await supabase
        ?.from('partner_categories')
        ?.update({ is_visible: isVisible })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) throw error;

      return convertToCamelCase(data);
    } catch (error) {
      console.error('Error toggling category visibility:', error);
      throw new Error(error?.message || 'Failed to toggle category visibility');
    }
  },
};

/**
 * Service for partner finder functionality
 */
export const partnerFinderService = {
  /**
   * Get all partners visible in partner finder
   */
  async getVisiblePartners() {
    try {
      const { data, error } = await supabase
        ?.from('partner_finder_view')
        ?.select('*')
        ?.order('name', { ascending: true });

      if (error) throw error;

      return data?.map(partner => ({
        id: partner?.id,
        name: partner?.name,
        city: partner?.city,
        street: partner?.street,
        postalCode: partner?.postal_code,
        phone: partner?.phone,
        email: partner?.email,
        website: partner?.website,
        logo: partner?.logo,
        logoAlt: partner?.logo_alt,
        logoBackgroundColor: partner?.logo_background_color,
        partnerCategoryId: partner?.partner_category_id,
        categoryName: partner?.category_name,
        categoryIcon: partner?.category_icon,
        urlSlug: partner?.url_slug,
        latitude: partner?.latitude,
        longitude: partner?.longitude,
        createdAt: partner?.created_at,
      })) || [];
    } catch (error) {
      console.error('Error fetching visible partners:', error);
      throw new Error(error?.message || 'Failed to fetch visible partners');
    }
  },

  /**
   * Search partners by postal code and radius
   */
  async searchByPostalCode(postalCode, categoryId = null, radiusKm = null) {
    try {
      let query = supabase
        ?.from('partner_finder_view')
        ?.select('*');

      if (categoryId) {
        query = query?.eq('partner_category_id', categoryId);
      }

      const { data, error } = await query?.order('name', { ascending: true });

      if (error) throw error;

      let partners = data?.map(partner => ({
        id: partner?.id,
        name: partner?.name,
        city: partner?.city,
        street: partner?.street,
        postalCode: partner?.postal_code,
        phone: partner?.phone,
        email: partner?.email,
        website: partner?.website,
        logo: partner?.logo,
        logoAlt: partner?.logo_alt,
        logoBackgroundColor: partner?.logo_background_color,
        partnerCategoryId: partner?.partner_category_id,
        categoryName: partner?.category_name,
        categoryIcon: partner?.category_icon,
        urlSlug: partner?.url_slug,
        latitude: partner?.latitude,
        longitude: partner?.longitude,
        createdAt: partner?.created_at,
      })) || [];

      // If postal code and radius are provided, filter by geographic distance
      if (postalCode && radiusKm) {
        try {
          // Import geocoding service dynamically to avoid circular dependencies
          const { geocodingService } = await import('./geocodingService');
          
          // Get coordinates for the search postal code
          const searchCoords = await geocodingService?.geocodePostalCode(postalCode);
          
          // Filter partners by distance
          partners = partners
            ?.filter(partner => {
              // Skip partners without coordinates
              if (!partner?.latitude || !partner?.longitude) {
                return false;
              }
              
              // Calculate distance
              const distance = geocodingService?.calculateDistance(
                searchCoords?.latitude,
                searchCoords?.longitude,
                partner?.latitude,
                partner?.longitude
              );
              
              // Add distance to partner object for sorting
              partner.distance = distance;
              
              // Include if within radius
              return distance <= radiusKm;
            })
            ?.sort((a, b) => a?.distance - b?.distance); // Sort by distance
        } catch (geocodingError) {
          console.error('Geocoding error during search:', geocodingError);
          // If geocoding fails, fall back to simple postal code prefix match
          const postalPrefix = postalCode?.substring(0, 2);
          partners = partners?.filter(partner => 
            partner?.postalCode?.startsWith(postalPrefix)
          );
        }
      }

      return partners;
    } catch (error) {
      console.error('Error searching partners:', error);
      throw new Error(error?.message || 'Failed to search partners');
    }
  },
};